import { useMemo, useRef, useState } from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../components/risikoanalyseStep/tokens";
import BottomBar from "../components/risikoanalyseStep/BottomBar";
import QuestionBlock from "../components/risikoanalyseStep/QuestionBlock";
import { scrollFormToTop } from "../components/risikoanalyseStep/scrollToTop";
import { useAnswersChangeSignal } from "../components/risikoanalyseStep/useAnswersChangeSignal";
import type { Answers } from "../components/risikoanalyseStep/types";
import { transportStepFormSchema } from "../components/risikoTransportStep/schema";

const BOTTOM_BAR_RESERVE = 110;

const useStyles = makeStyles({
  shell: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: BRAND.bg,
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
  },
  shellEmbedded: {
    minHeight: "auto",
  },
  body: {
    flexGrow: 1,
    display: "flex",
    overflow: "hidden",
  },
  bodyEmbedded: {
    overflow: "visible",
  },
  main: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
  },
  mainEmbedded: {
    overflowY: "visible",
  },
  inner: {
    width: "100%",
    maxWidth: "880px",
    marginLeft: "auto",
    marginRight: "auto",
    paddingTop: "24px",
    paddingLeft: "16px",
    paddingRight: "16px",
    paddingBottom: `${BOTTOM_BAR_RESERVE}px`,
    flexGrow: 1,
    "@media (min-width: 768px)": {
      paddingLeft: "32px",
      paddingRight: "32px",
    },
  },
  innerEmbedded: {
    // BottomBar is sticky and in flow, so no extra reserve needed
    paddingBottom: 0,
  },
  sectionPill: {
    display: "inline-block",
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "10px",
    paddingRight: "10px",
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: BRAND.white,
    marginBottom: "8px",
    ...shorthands.borderRadius("999px"),
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    columnGap: "16px",
    flexWrap: "wrap",
  },
  title: {
    margin: 0,
    marginTop: "4px",
    fontFamily: BRAND.fontAccent,
    fontSize: "clamp(1.6rem, 4vw, 2.4rem)",
    fontWeight: 400,
    color: BRAND.ink,
    lineHeight: 1.15,
    flexGrow: 1,
    minWidth: "200px",
  },
  description: {
    marginTop: "8px",
    marginBottom: 0,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.muted,
  },
  questions: {
    marginTop: "16px",
  },
});

export type RisikoTransportStepFormProps = {
  chrome?: "embedded";
  initialAnswers?: Answers;
  onSubmitEmbedded?: (answers: Answers) => Promise<void> | void;
  embeddedSubmitLabel?: string;
  /**
   * Feuert bei jeder Antwort-Änderung — NewRiskPage meldet daraus
   * ungespeicherte Eingaben ans Fenster. Beim Mount bewusst NICHT gefeuert.
   */
  onAnswersChange?: (answers: Answers) => void;
};

const RisikoTransportStepForm = ({
  initialAnswers,
  onSubmitEmbedded,
  embeddedSubmitLabel = "Transport-Risiko speichern",
  onAnswersChange,
}: RisikoTransportStepFormProps) => {
  const styles = useStyles();

  const [answers, setAnswers] = useState<Answers>(initialAnswers ?? {});
  useAnswersChangeSignal(answers, onAnswersChange);
  const [sectionIdx, setSectionIdx] = useState(0);
  const [subStepIdx, setSubStepIdx] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const mainRef = useRef<HTMLDivElement | null>(null);

  const handleAnswerChange = (key: string, value: unknown) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const totalSubSteps = useMemo(
    () =>
      transportStepFormSchema.sections.reduce(
        (acc, sec) => acc + sec.subSteps.length,
        0,
      ),
    [],
  );
  const flatIndex = useMemo(() => {
    let i = 0;
    for (let s = 0; s < sectionIdx; s += 1)
      i += transportStepFormSchema.sections[s].subSteps.length;
    return i + subStepIdx;
  }, [sectionIdx, subStepIdx]);
  const isLastStep = flatIndex === totalSubSteps - 1;
  const canPrev = flatIndex > 0;
  const stepFraction = (flatIndex + 1) / totalSubSteps;

  const goPrev = () => {
    if (subStepIdx > 0) setSubStepIdx((s) => s - 1);
    else if (sectionIdx > 0) {
      const prev = sectionIdx - 1;
      setSectionIdx(prev);
      setSubStepIdx(transportStepFormSchema.sections[prev].subSteps.length - 1);
    }
    scrollFormToTop();
  };

  const goNext = () => {
    const sec = transportStepFormSchema.sections[sectionIdx];
    if (subStepIdx + 1 < sec.subSteps.length) setSubStepIdx((s) => s + 1);
    else if (sectionIdx + 1 < transportStepFormSchema.sections.length) {
      setSectionIdx((s) => s + 1);
      setSubStepIdx(0);
    }
    scrollFormToTop();
  };

  const handleSubmit = async () => {
    if (!onSubmitEmbedded) return;
    setSubmitting(true);
    try {
      await onSubmitEmbedded(answers);
    } finally {
      setSubmitting(false);
    }
  };

  const section = transportStepFormSchema.sections[sectionIdx];
  const subStep = section.subSteps[subStepIdx];

  return (
    <div className={mergeClasses(styles.shell, styles.shellEmbedded)}>
      <div className={mergeClasses(styles.body, styles.bodyEmbedded)}>
        <main className={mergeClasses(styles.main, styles.mainEmbedded)} ref={mainRef}>
          <div className={mergeClasses(styles.inner, styles.innerEmbedded)}>
            <div className={styles.titleRow}>
              <h2 className={styles.title}>{subStep.title}</h2>
            </div>
            {subStep.description && (
              <p className={styles.description}>{subStep.description}</p>
            )}
            <div className={styles.questions}>
              {subStep.questions.map((q, idx) => (
                <QuestionBlock
                  key={`${subStep.key}-${idx}`}
                  question={q}
                  answers={answers}
                  onChange={handleAnswerChange}
                />
              ))}
            </div>
            <BottomBar
              sticky
              onPrev={goPrev}
              onNext={goNext}
              onSubmit={handleSubmit}
              onSaveDraft={() => {
                /* embedded: no draft */
              }}
              canPrev={canPrev}
              isLastStep={isLastStep}
              submitting={submitting}
              savingDraft={false}
              draftSavedAt={null}
              progressLabel={`Schritt ${flatIndex + 1} von ${totalSubSteps}`}
              progressFraction={stepFraction}
              submitLabel={embeddedSubmitLabel}
              showSaveDraft={false}
            />
          </div>
        </main>
      </div>
    </div>
  );
};

export { RisikoTransportStepForm };
